import { formatHashtag } from './hashtags'

/**
 * Hierarchical tags are stored as one row per segment: each tag's `name` holds
 * only its own segment (e.g. "project") and `parentId` points at its parent.
 * The human-facing "handle" is the full path with a leading `#`, e.g.
 * `#work/project/subtask`. These helpers rebuild that handle from a flat tag
 * list so API consumers (especially MCP agents) get the path without having to
 * walk the parent chain themselves.
 */

/** Minimal shape needed to resolve a tag's hierarchical handle. */
export interface TagNode {
  id: string
  name: string
  parentId?: string | null
}

/**
 * Build the hierarchical handle for a single tag, e.g. `#work/project/subtask`.
 * Walks the parent chain via `byId`. A missing ancestor simply truncates the
 * chain (the deepest resolvable prefix is used). Guards against cycles so a
 * corrupt parent loop can't hang the caller.
 */
export function buildTagHandle(tag: TagNode, byId: Map<string, TagNode>): string {
  const segments: string[] = []
  const seen = new Set<string>()
  let current: TagNode | undefined = tag
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    segments.unshift(current.name)
    current = current.parentId ? byId.get(current.parentId) : undefined
  }
  return formatHashtag(segments.join('/'))
}

/**
 * Build handles for a list of tags. Uses the same list to resolve ancestors,
 * so pass every tag whose ancestors might be referenced (typically all of the
 * user's tags). Returns a Map from tag id to its handle.
 */
export function buildTagHandles(allTags: TagNode[]): Map<string, string> {
  const byId = new Map<string, TagNode>(allTags.map((t) => [t.id, t]))
  const handles = new Map<string, string>()
  for (const tag of allTags) {
    handles.set(tag.id, buildTagHandle(tag, byId))
  }
  return handles
}
